import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";
import { useVaultStore } from "@/stores/billing/vault-store";
import type { CreditTransactionType } from "@/types/billing";

/**
 * TransactionHistory
 * Paginated transaction ledger showing credit transactions with type and vault filtering.
 */

interface TransactionHistoryProps {
  orgId?: string; // Deprecated: orgId now derived from org context header
}

const TRANSACTION_TYPE_LABELS: Record<CreditTransactionType, string> = {
  plan_allocation: "Plan Allocation",
  plan_reset: "Plan Reset",
  ai_usage: "AI Usage",
  extra_purchase: "Extra Purchase",
  extra_usage: "Extra Usage",
  vault_reallocation: "Reallocation",
  refund: "Refund",
};

const POSITIVE_TYPES: Set<string> = new Set([
  "plan_allocation",
  "extra_purchase",
  "refund",
]);

function getTypeBadgeColor(type: CreditTransactionType): string {
  if (POSITIVE_TYPES.has(type)) {
    return "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300";
  }
  return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
}

function formatCents(cents: number): string {
  const dollars = Math.abs(cents) / 100;
  const prefix = cents >= 0 ? "+" : "-";
  return `${prefix}$${dollars.toFixed(2)}`;
}

function formatBalance(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

const ALL_TYPES: CreditTransactionType[] = [
  "plan_allocation",
  "plan_reset",
  "ai_usage",
  "extra_purchase",
  "extra_usage",
  "vault_reallocation",
  "refund",
];

export const TransactionHistory: React.FC<TransactionHistoryProps> = () => {
  const {
    transactions,
    transactionsPagination,
    vaults,
    isLoading,
    fetchTransactions,
  } = useVaultStore();

  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [vaultFilter, setVaultFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const limit = 20;

  const loadTransactions = useCallback(() => {
    const filters: { page?: number; limit?: number; type?: string; vaultId?: string } = {
      page,
      limit,
    };
    if (typeFilter !== "all") filters.type = typeFilter;
    if (vaultFilter !== "all") filters.vaultId = vaultFilter;

    fetchTransactions(filters);
  }, [page, typeFilter, vaultFilter, fetchTransactions, limit]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const handleTypeChange = (value: string) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handleVaultChange = (value: string) => {
    setVaultFilter(value);
    setPage(1);
  };

  const totalPages = transactionsPagination?.totalPages || 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Transaction History
        </CardTitle>
        <CardDescription>
          View all credit transactions across your organization.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="w-48">
            <Select value={typeFilter} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ALL_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {TRANSACTION_TYPE_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Select value={vaultFilter} onValueChange={handleVaultChange}>
              <SelectTrigger>
                <SelectValue placeholder="All Vaults" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vaults</SelectItem>
                {vaults.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        {isLoading && transactions.length === 0 ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : transactions.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No transactions found.
          </p>
        ) : (
          <>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Balance After</TableHead>
                    <TableHead>Vault</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => {
                    const isPositive = POSITIVE_TYPES.has(tx.type);
                    const vaultName = vaults.find((v) => v.id === tx.vaultId)?.name;
                    return (
                      <TableRow key={tx.id}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {formatDate(tx.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="secondary"
                            className={cn("text-xs", getTypeBadgeColor(tx.type))}
                          >
                            {TRANSACTION_TYPE_LABELS[tx.type] || tx.type}
                          </Badge>
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-mono text-sm font-medium",
                            isPositive
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400",
                          )}
                        >
                          {formatCents(tx.amountCents)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-muted-foreground">
                          {formatBalance(tx.balanceAfterCents)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {vaultName || (tx.vaultId ? tx.vaultId.slice(-6) : "--")}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">
                          {tx.description || "--"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {transactionsPagination
                  ? `${transactionsPagination.total} total transactions`
                  : ""}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1 || isLoading}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages || isLoading}
                  className="gap-1"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
